// MainPage.tsx
import { useState, useEffect } from "react";
import { ImageData } from "../../interfaces/imageData";
import { DataArea } from "../../interfaces/dataArea";
import { Labelme } from "../../interfaces/labelme";
import { Size } from "../../interfaces/size";

import Loader from "../../labelmeComponents/Loader/loader";
import Displayer from "../../labelmeComponents/Displayer/displayer";
import Canvas from "../../labelmeComponents/Canvas/canvas";
import Box from "../../labelmeComponents/Box/box";
import Exporter from "../../labelmeComponents/Exporter/exporter";
import { ReservedKeyword } from "../../interfaces/reservedKeyword";
import { overrideDefaultConfigWithReservedKeywordConfig, clipPolygonAgainstExisting } from "../../utils/draw";
import CollapseBox from "../../labelmeComponents/CollapseBox/collapseBox";
import { getLabelmeImages } from '../../utils/api';
import DropdownFranchise from "../../components/dropdowns/DropdownFranchise";
import DropdownLocationSingle from "../../components/dropdowns/DropdownLocationSingle";
import { useData } from "../../hooks/useData";
import '../../css/labelme.css';
import ActionsButton from '../../components/buttons/ActionsButton';
import { v4 as uuidv4 } from 'uuid';
import { generateRandomColor } from "../../utils/draw";

const MainPage = () => {
    const reservedKeywords: ReservedKeyword = {
        "blackout": {
            polygonColor: "rgba(0,0,0,1)",
            description: "Reserved keyword that blacks out the polygon. Color cannot be modified"
        },
        "ginger": {
            polygonColor: "rgba(235, 47, 6, 1.0)",
            description: "Reserved keyword that sets the color to an intense tomato red. Color cannot be modified"
        }
    };

    const {
        selectedFranchise,
        selectedLocation
    } = useData();

    const [canvasSize, setCanvasSize] = useState<Size<number> | undefined>(undefined);
    const [imageData, setImageData] = useState<ImageData | undefined>(undefined);
    const [dataAreas, setDataAreas] = useState<Array<DataArea> | undefined>([]);
    const [editedDataArea, setEditedDataArea] = useState<DataArea | undefined>(undefined);
    const [dataToExport, setDataToExport] = useState<Labelme | undefined>(undefined);
    const [mode, setMode] = useState<string | undefined>(undefined);
    const [fileName, setFileName] = useState<string>("enter");
    const [loadedImages, setLoadedImages] = useState<Map<string, string>>(new Map());
    const [noOverlapMode, setNoOverlapMode] = useState<boolean>(true);
    const [otherFilePullNumbers, setOtherFilePullNumbers] = useState<number[]>([]);

    const fetchImages = async (franchiseId: number, locationId: number) => {
        const images = await getLabelmeImages(franchiseId, locationId);
        setLoadedImages(images);
    };

    useEffect(() => {
        if (selectedFranchise && selectedLocation){
            fetchImages(selectedFranchise['id'] as unknown as number, selectedLocation['id'] as unknown as number);
        }
    }, [selectedLocation]);

    // When fileName is pull or pull1, load the OTHER file's pull numbers
    useEffect(() => {
        if (fileName !== 'pull' && fileName !== 'pull1') {
            setOtherFilePullNumbers([]);
            return;
        }

        const otherFileName = fileName === 'pull' ? 'pull1' : 'pull';
        const keyFragment = otherFileName + '.json';

        let found = false;
        for (const key of loadedImages.keys()) {
            if (key.includes(keyFragment)) {
                found = true;
                const url = loadedImages.get(key);
                if (url) {
                    fetch(url)
                        .then(res => res.json())
                        .then((json: Labelme) => {
                            const numbers: number[] = [];
                            json.shapes.forEach(shape => {
                                const match = shape.label.match(/^pull_(\d+)$/);
                                if (match) {
                                    numbers.push(parseInt(match[1]));
                                }
                            });
                            setOtherFilePullNumbers(numbers);
                        })
                        .catch(err => {
                            console.error('Error loading other pull file:', err);
                            setOtherFilePullNumbers([]);
                        });
                }
                break;
            }
        }

        if (!found) {
            setOtherFilePullNumbers([]);
        }
    }, [fileName, loadedImages]);

    const handleCanvasSize = (canvasSize: Size<number> | undefined) => {
        setCanvasSize(canvasSize);
    };

    const handleLoadImage = (imageData: ImageData | undefined) => {
        setImageData(imageData);
        if (imageData === undefined) {
            setDataAreas([]);
        }
    };

    const handleDataAreas = (dataAreas: Array<DataArea> | undefined) => {
        setDataAreas(dataAreas);
    };

    const handleMode = (mode: string) => {
        setMode(mode);
    };

    const updateDataAreas = (dataArea: DataArea) => {
        if (dataAreas !== undefined && dataAreas.length > 0) {
            dataAreas.forEach((da) => overrideDefaultConfigWithReservedKeywordConfig(da, reservedKeywords));
            let updatedDataAreas = [...dataAreas];
            if (noOverlapMode) {
                const clippedPolygons = clipPolygonAgainstExisting(dataArea.polygon, dataAreas, dataArea.id);
                if (clippedPolygons.length === 0) {
                    setMode("Waiting");
                    setEditedDataArea(undefined);
                    return;
                }
                const idx = updatedDataAreas.findIndex(x => x.id === dataArea.id);
                if (idx !== -1) {
                    updatedDataAreas[idx].polygon = clippedPolygons[0];
                    if (clippedPolygons.length > 1) {
                        const extras = clippedPolygons.slice(1).map((poly, index) => ({
                            id: uuidv4(),
                            label: `${dataArea.label}_${index + 1}`,
                            color: generateRandomColor(0.5),
                            polygon: poly
                        }));
                        updatedDataAreas = [...updatedDataAreas, ...extras];
                    }
                }
            } else {
                const idx = updatedDataAreas.findIndex(x => x.id === dataArea.id);
                if (idx !== -1) {
                    updatedDataAreas[idx] = dataArea;
                }
            }
            setMode("Waiting");
            setEditedDataArea(undefined);
            setDataAreas(updatedDataAreas);
        }
    };

    const editDataArea = (dataArea: DataArea) => {
        setMode(`Editing Data Area with label: ${dataArea.label}`);
        setEditedDataArea(dataArea);
    };

    const deleteDataAreaFromDataAreas = (dataArea: DataArea) => {
        if (dataAreas !== undefined && dataAreas.length > 0) {
            setDataAreas([...dataAreas.filter((x) => x.id !== dataArea.id)]);
        }
    };

    const exportDataAreas = (labelme: Labelme) => {
        setDataToExport(labelme);
    };

    return (
        <div className="main-container">
            <div className="main-content">
                <div className="row">
                    <div className="col-10 col-m-10 p-2 mx-auto">
                        <DropdownFranchise />
                        <DropdownLocationSingle />
                        {selectedLocation && selectedLocation.id && (
                            <ActionsButton
                                locationId={selectedLocation.id}
                                onMenuClose={() => {
                                }}
                            />
                        )}
                    </div>
                </div>

                <div className="row">
                    <div className="col-10 col-m-10 p-2 mx-auto">
                        <CollapseBox
                            text="How to label?"
                            backgroundColor="#636e72"
                            borderColor="#2d3436"
                        >
                            <br />
                            <p className="color-white font-small font-lighter">select a franchise and location from the dropdown up top (if you dont see any, refresh or contact your admin to be given locations)</p>
                            <br />
                            <p className="color-white font-small font-lighter">images should show up one on top of the other. inspect these images, and find the one you want to draw on.</p>
                            <br />
                            <p className="color-white font-small font-lighter">depending on which image you choose, choose the corresponding fileName from "enter: orderboard, exit: pickup window, pull: the final or only pull forward, pull1: the first pull forward if there are two, lobby_areas: lobby area, mobile: mobile orderingg spots</p>
                            <br />
                            <p className="color-white font-small font-lighter">WHICHEVER FILENAME YOU PRESS LAST WILL BE THE LOCATION IT IS SENT TO... if you pressed the wrong fileName, just delete your labels and press the correct one, but if you export and send the wrong data to aws, that wouldnt be great.</p>
                            <br />
                            <p className="color-white font-small font-lighter">depending on which fileName is chosen, as you draw, a different set of labels will be drawn. pull, pull1, and mobile will increment from either pull or mobile 1-n. enter will do orderboard1, preorderboard1, then 2, etc...</p>
                            <br />
                            <p className="color-white font-small font-lighter">you can toggle between "set blackout" and "set label" to either draw the corresponding labels or draw blackout regions</p>
                            <br />
                            <p className="color-white font-small font-lighter">note that when fileName is changed, "set blackout" will be changed to "set label"</p>
                            <br />
                            <br />
                            <p className="color-white font-small font-lighter">If you are in draw mode, right click to erase points</p>
                            <br />
                            <p className="color-white font-small font-lighter">To close a shape, you must finish clicking the starting point. It will highlight in yellow when the mouse is over</p>
                            <br />
                            <p className="color-white font-small font-lighter">To edit the shape, once you click edit in the labels, you can go to the canvas and edit the points</p>
                            <br />
                            <br />
                            <p className="color-white font-small font-lighter">once you are done drawing the labels and editing them, you can delete anything you didnt want (such as preorderboard areas in enter)</p>
                            <br />
                            <p className="color-white font-small font-lighter">press export. this will show you the json (the data of the file that you have created)</p>
                            <br />
                            <p className="color-white font-small font-lighter">if you are happy with what you have produced and are confident that the fileName is correct, press "send to aws"</p>
                            <br />
                            <p className="color-white font-small font-lighter">you are now free to press on the next picture you would like to work on.</p>
                        </CollapseBox>
                    </div>
                </div>

                <div className="row">
                    <div className="col-10 col-m-10 p-2 mx-auto">
                        <p className="color-white font-small">Operation: <b><i>{mode ?? "Waiting"}</i></b></p>
                    </div>
                </div>

                <div className="row">
                    <div className="col-10 col-m-10 p-2 mx-auto">
                        <label className="color-white font-small">
                            <input
                                type="checkbox"
                                checked={noOverlapMode}
                                onChange={(e) => setNoOverlapMode(e.target.checked)}
                            />
                            No Overlap Mode
                        </label>
                    </div>
                </div>

                <div className="row">
                    <div className="col-10 col-m-10 p-2 mx-auto">
                        {imageData !== undefined ? (
                            <Canvas
                                imageData={imageData}
                                dataAreas={dataAreas ?? []}
                                editedDataArea={editedDataArea}
                                sendDataArea={handleDataAreas}
                                handleCanvasSize={handleCanvasSize}
                                handleMode={handleMode}
                                fileName={fileName}
                                noOverlapMode={noOverlapMode}
                                otherFilePullNumbers={otherFilePullNumbers}
                            />
                        ) : (
                            <Box text="Waiting for image" backgroundColor="rgba(116, 185, 255, 0.4)" borderColor="rgba(116, 185, 255, 0.4)" />
                        )}
                    </div>
                </div>

                <div className="row">
                    <div className="col-10 col-m-10 p-2 mx-auto">
                        <Displayer
                            dataAreas={dataAreas}
                            editedDataArea={editedDataArea}
                            configuration={reservedKeywords}
                            updateDataAreas={updateDataAreas}
                            deleteDataAreaFromDataAreas={deleteDataAreaFromDataAreas}
                            editDataArea={editDataArea}
                        />
                    </div>
                </div>

                <div className="row" id="jsonViewer">
                    <div className="col-10 col-m-10 p-2 mx-auto">
                        <Exporter 
                            dataToExport={dataToExport} 
                            fileName={fileName}
                        />
                    </div>
                </div>
            </div>

            <div className="loader-container">
                <Loader
                    dataAreas={dataAreas}
                    canvasSize={canvasSize}
                    configuration={reservedKeywords}
                    loadDataAreas={handleDataAreas}
                    loadImageData={handleLoadImage}
                    exportDataArea={exportDataAreas}
                    fileName={fileName}
                    setFileName={setFileName}
                    loadedImages={loadedImages}
                    location={selectedLocation && selectedLocation.id ? selectedLocation.id : null}
                />
            </div>
        </div>
    );
};

export default MainPage;